import { ChangeDetectionStrategy, Component, computed, input, OnInit, signal } from '@angular/core';

import { injectModalRef } from '../../../../shared/modal';
import { IMAGE_TIPS, ImageView } from '../image-details';

export interface ImageLightboxData {
  /** The images in the gallery's current order (after any site filter). */
  views: ImageView[];
  /** Position in `views` to open at. */
  start: number;
}

/**
 * One /image result, large, with everything the response says about it
 * beside it: where it appeared, its caption, what the analysis found.
 * ← / → (and the buttons) step through the gallery without closing.
 */
@Component({
  selector: 'app-image-lightbox',
  standalone: false,
  templateUrl: './image-lightbox.html',
  styleUrl: './image-lightbox.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '(keydown)': 'onKeydown($event)' },
})
export class ImageLightbox implements OnInit {
  readonly data = input.required<ImageLightboxData>();
  readonly modalRef = injectModalRef<void>();

  protected readonly position = signal(0);
  /** 0 = the stored copy, 1 = the publisher's original, 2 = neither loaded. */
  protected readonly attempt = signal(0);
  protected readonly tips = IMAGE_TIPS;

  protected readonly view = computed(() => this.data().views[this.position()]);
  protected readonly total = computed(() => this.data().views.length);

  protected readonly src = computed(() => {
    const hit = this.view().hit;
    return this.attempt() === 1 ? hit.metadata?.originalUrl ?? hit.url : hit.url;
  });

  /** Stored copy failed: try the original once, then give up. */
  protected imageFailed(): void {
    const original = this.view().hit.metadata?.originalUrl;
    this.attempt.update(a => (a === 0 && original && original !== this.view().hit.url ? 1 : 2));
  }

  ngOnInit(): void {
    this.position.set(this.data().start);
  }

  protected step(by: number): void {
    const next = this.position() + by;
    if (next < 0 || next >= this.total()) return;
    this.position.set(next);
    this.attempt.set(0);
  }

  protected onKeydown(event: KeyboardEvent): void {
    // Leave arrows alone inside anything that uses them itself.
    if ((event.target as Element).closest('input, textarea, select')) return;
    if (event.key === 'ArrowRight') { this.step(1); event.preventDefault(); }
    if (event.key === 'ArrowLeft') { this.step(-1); event.preventDefault(); }
  }
}
